<template>
    <div class="c-o-my-pages-section">
        <div
            class="c-o-my-pages-section__inner c-o-my-pages-section__inner--large"
        >
            <div class="o-wysiwyg">
                <h2 class="c-o-my-pages-section__heading">Förfrågningar</h2>
                <p>
                    Du ser dina matchade förfrågningar under fliken
                    <i>Obesvarade</i> och de förfrågningar du besvarat under
                    fliken <i>Besvarade</i>.
                </p>
                <h3 class="h4" v-if="showHelp">
                    Mer information <a @click="showHelp = false">(Dölj)</a>
                </h3>
                <h3 class="h4" v-else>
                    Mer information <a @click="showHelp = true">(Visa)</a>
                </h3>
                <ul class="c-o-my-pages-section__message" v-if="showHelp">
                    <li>
                        <div class="statusPillWrapper">
                            <status-pill :status="'active'"
                                >TryggtJobb</status-pill
                            >
                            – Du har blivit utvald till ett TryggtJobb som
                            innebär att den valda hantverkaren och beställaren
                            kommer att signera ett digitalt avtal.
                        </div>
                        <div class="statusPillWrapper">
                            <status-pill :status="'contract'"
                                >Begärt avtal</status-pill
                            >
                            – Beställaren har valt dig och väntar ett
                            avtalsförslag från dig. Välj Skapa avtal i den
                            aktuella förfrågan och fyll i den digitala
                            avtalsmallen så skickas ett avtalsförslag på mail
                            till beställaren.
                        </div>
                        <div class="statusPillWrapper">
                            <status-pill :status="'direct'"
                                >DirektFörfrågan</status-pill
                            >
                            – Beställaren har valt ut ert företag till sin
                            förfrågan.
                        </div>
                    </li>
                </ul>
                <ul class="c-o-my-pages-section__message" v-if="showHelp">
                    <li>
                        Gul bakgrund innebär att förfrågan är yngre än 5 dagar
                        och att du har förtur.
                    </li>
                </ul>
                <ul class="c-o-my-pages-section__message" v-if="showHelp">
                    <li>
                        <div class="c-o-my-pages-section__message-circles-wrap">
                            <span
                                class="c-o-my-pages-section__message-circle-wrap"
                            >
                                <status-circle :value="'green'"></status-circle>
                                0-3 företag har svarat kunden
                            </span>
                            <span
                                class="c-o-my-pages-section__message-circle-wrap"
                            >
                                <status-circle
                                    :value="'yellow'"
                                ></status-circle>
                                4-5 företag har svarat kunden
                            </span>
                            <span
                                class="c-o-my-pages-section__message-circle-wrap"
                            >
                                <status-circle :value="'red'"></status-circle>
                                Förfrågan har redan fått max antal svar eller så
                                har beställaren inaktiverat den.
                            </span>
                        </div>
                    </li>
                </ul>
                <p class="u-margin-vertical-2" v-if="requestMeta">
                    <span class="u-margin-right-2"
                        >Antal matchande förfrågningar:</span
                    >
                    <b>1 månad:</b>
                    <span class="u-margin-right-2">{{
                        requestMeta.LastMonth
                    }}</span>
                    <b>3 månader:</b>
                    <span class="u-margin-right-2">{{
                        requestMeta.LastThreeMonth
                    }}</span>
                    <b>12 månader:</b>
                    <span>{{ requestMeta.LastYear }}</span>
                </p>
            </div>
            <div class="c-m-tabs">
                <div class="c-m-tabs__titles">
                    <div
                        class="c-m-tabs__title"
                        :class="{
                            'c-m-tabs__title--active':
                                activeTab === 'unanswered'
                        }"
                        @click="switchToTab('unanswered')"
                    >
                        Obesvarade
                    </div>
                    <div
                        class="c-m-tabs__title"
                        :class="{
                            'c-m-tabs__title--active': activeTab === 'answered'
                        }"
                        @click="switchToTab('answered')"
                    >
                        Besvarade
                    </div>
                </div>
                <div class="c-m-tabs__contents">
                    <div
                        v-show="activeTab === 'unanswered'"
                        class="c-m-tabs__content"
                    >
                        <requests-list-table
                            class="c-o-table--margin-bottom"
                            :isMatched="true"
                            :loading="unansweredLoading"
                            :error="unansweredError"
                            :requests="unansweredRequests"
                            :highlightNew="!isTrialUser"
                            :emptyState="{
                                body:
                                    'Det finns inga förfrågningar att visa här än.',
                                icon: ''
                            }"
                        ></requests-list-table>
                        <pagination
                            :totalItemsProp="unansweredCount"
                            :itemsPerPageProp="25"
                            :maxPagesToShowProp="5"
                            :currentPageProp="unansweredPage"
                            @changed="setUnansweredPageAndFetch"
                        ></pagination>
                    </div>
                    <div
                        v-show="activeTab === 'answered'"
                        class="c-m-tabs__content"
                    >
                        <requests-list-table
                            class="c-o-table--margin-bottom"
                            :isMatched="true"
                            :loading="answeredLoading"
                            :error="answeredError"
                            :requests="answeredRequests"
                            :highlightNew="false"
                            :isTrialUser="isTrialUser"
                            :emptyState="{
                                body:
                                    'Här dyker förfrågningar du har besvarat upp. Just nu finns dock inga att visa.',
                                icon: ''
                            }"
                        ></requests-list-table>
                        <pagination
                            :totalItemsProp="answeredCount"
                            :itemsPerPageProp="25"
                            :maxPagesToShowProp="5"
                            :currentPageProp="answeredPage"
                            @changed="setAnsweredPageAndFetch"
                        ></pagination>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters } from "vuex";

import {
    ANSWERED_REQUESTS_LOADING,
    FETCH_COMPANY_ANSWERED_REQUESTS,
    FETCH_COMPANY_REQUESTS,
    GET_ANSWERED_REQUESTS_ERROR,
    GET_ANSWERED_REQUESTS_PAGE,
    GET_COMPANY_REQUESTS,
    GET_IS_TRIAL_USER,
    GET_REQUESTS_ERROR,
    GET_REQUESTS_PAGE,
    GET_REQUEST_META,
    REQUESTS_LOADING,
    SET_ANSWERED_REQUESTS_PAGE,
    SET_REQUESTS_PAGE
} from "../../../../../../store/store-models/my-pages-company-model";

import StatusPill from "../../../../atoms/AtomStatus.vue";
import StatusCircle from "../../../../atoms/AtomStatusCircle.vue";
import Pagination from "../../../../molecules/MoleculePagination.vue";
import RequestsListTable from "../../../OrganismRequestsListCompanyTable.vue";

export default {
    components: {
        RequestsListTable,
        StatusPill,
        Pagination,
        StatusCircle
    },
    data() {
        const queryString = window.location.search;
        const urlParams = new URLSearchParams(queryString);
        let tab = urlParams.get("tab");
        let page = urlParams.get("page");
        if (!tab) {
            tab = "unanswered";
        }

        if (!page) {
            page = 1;
        }

        if (tab === "answered") {
            this.$store.commit(SET_ANSWERED_REQUESTS_PAGE, page);
            this.$store.dispatch(FETCH_COMPANY_ANSWERED_REQUESTS);
        } else {
            this.$store.commit(SET_REQUESTS_PAGE, page);
            this.$store.dispatch(FETCH_COMPANY_REQUESTS);
        }

        return {
            showHelp: false,
            showAll: false,
            activeTab: tab
        };
    },
    computed: {
        ...mapGetters({
            unansweredLoading: REQUESTS_LOADING,
            unansweredError: GET_REQUESTS_ERROR,
            unansweredPage: GET_REQUESTS_PAGE,

            answeredLoading: ANSWERED_REQUESTS_LOADING,
            answeredError: GET_ANSWERED_REQUESTS_ERROR,
            answeredPage: GET_ANSWERED_REQUESTS_PAGE,

            requests: GET_COMPANY_REQUESTS,
            requestMeta: GET_REQUEST_META,

            isTrialUser: GET_IS_TRIAL_USER
        }),
        unansweredRequests: function() {
            return !this.unansweredLoading && !this.unansweredError
                ? this.requests.Unanswered.data
                : [];
        },
        unansweredCount: function() {
            return !this.unansweredLoading && !this.unansweredError
                ? this.requests.Unanswered.count
                : 0;
        },
        answeredRequests: function() {
            var arr =
                !this.answeredLoading && !this.answeredError
                    ? this.requests.Answered.data
                    : [];
            return arr;
        },
        answeredCount: function() {
            var arr =
                !this.answeredLoading && !this.answeredError
                    ? this.requests.Answered.count
                    : 0;
            return arr;
        }
    },
    methods: {
        switchToTab: function(newTab) {
            if (newTab === "answered") {
                this.$store.commit(SET_ANSWERED_REQUESTS_PAGE, 1);
                this.$store.dispatch(FETCH_COMPANY_ANSWERED_REQUESTS);
            } else {
                this.$store.commit(SET_REQUESTS_PAGE, 1);
                this.$store.dispatch(FETCH_COMPANY_REQUESTS);
            }
            this.activeTab = newTab;
            let tab = "?tab=" + newTab + "&page=1";
            history.pushState({ page: 1 }, "title 1", tab);
        },
        setUnansweredPageAndFetch: function(page) {
            this.$store.commit(SET_REQUESTS_PAGE, page);
            this.$store.dispatch(FETCH_COMPANY_REQUESTS);
        },
        setAnsweredPageAndFetch: function(page) {
            this.$store.commit(SET_ANSWERED_REQUESTS_PAGE, page);
            this.$store.dispatch(FETCH_COMPANY_ANSWERED_REQUESTS);
        }
    }
};
</script>
<style scoped>
.statusPillWrapper {
    margin-bottom: 1em;
}
</style>



// WEBPACK FOOTER //
// src/views/components/organisms/my-pages-sections/company/requests/OrganismMyPagesRequestsListCompany.vue